import { Button, Input, Modal, Typography, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { ApplicationsFilter } from '../../../shared/VolunteerApplicationServiceTypes';
import { useCallback, useState } from 'react';
import { VALID_ABHYASI_ID_REGEX } from '../../../shared/Constants';
import { UndoOutlined } from '@ant-design/icons';
import FilterIcon from '../../../assets/images/filter-icon.svg';

interface FilterProps {
	value: ApplicationsFilter;
	onFilterChange: (value: ApplicationsFilter) => void;
}

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const FilterProposerRegion = ({
	value: { searchByProposerName },
	onFilterChange,
}: FilterProps) => {
	const { t, i18n } = useTranslation();
	const lengthyLanguages = ['ta', 'te', 'ka', 'ma'];
	const isLengthyLanguage = lengthyLanguages.includes(i18n.language);
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;
	const [proposerNameValue, setProposerNameValue] = useState<
		string | undefined
	>(searchByProposerName);
	const [srcmIdProposer, setSrcmIdProposer] = useState<string | undefined>();
	const [isFilterPopup, setIsFilterPopup] = useState<boolean>(false);
	const [isFilterTextApplied, setIsFilterTextApplied] =
		useState<boolean>(false);
	const [proposerSrcmIdError, setSrcmIdProposerError] = useState('');

	const fireFilterChange = useCallback(() => {
		onFilterChange({
			searchByProposerSrcmId: srcmIdProposer,
			searchByProposerName: proposerNameValue,
		});
	}, [onFilterChange, srcmIdProposer, proposerNameValue]);

	const handleFilterButtonClick = useCallback(() => {
		fireFilterChange();
		setIsFilterPopup(false);
		setIsFilterTextApplied(true);
	}, [fireFilterChange]);

	const handleResetFilters = useCallback(() => {
		setProposerNameValue('');
		setSrcmIdProposer('');
		setSrcmIdProposerError('');
		onFilterChange({});

		setIsFilterPopup(false);
		setIsFilterTextApplied(false);
	}, [onFilterChange]);

	const onProposerNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setProposerNameValue(e.target.value);
		},
		[]
	);

	const onProposerSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value.toUpperCase();
			setSrcmIdProposer(value);
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setSrcmIdProposerError('invalidAbhyasiId');
			} else {
				setSrcmIdProposerError('');
			}
		},
		[]
	);

	const handleFilterPopup = useCallback(() => {
		setIsFilterPopup(true);
	}, []);

	const onClose = useCallback(() => {
		setIsFilterPopup(false);
	}, []);

	return (
		<>
			<div className="flex flex-row h-full min-h-0 gap-2">
				<div className="flex justify-end w-1/6 sm:w-auto">
					<Button
						className="block sm:hidden"
						variant="outlined"
						onClick={handleFilterPopup}
					>
						<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
					</Button>
					<Button
						className={`hidden sm:block text-primary-blue font-sans font-bold text-sm border border-solid mt-0.5 ${
							isFilterTextApplied ? 'border-primary-blue' : 'border-gray-200'
						}`}
						variant="outlined"
						onClick={handleFilterPopup}
					>
						{isFilterTextApplied ? t('filterApplied') : t('filter')}
					</Button>

					{isFilterTextApplied ? (
						<Button
							className="hidden sm:block flex-1 h-9 text-red-500 font-sans font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					) : (
						''
					)}
				</div>
				<Modal
					open={isFilterPopup}
					onCancel={onClose}
					footer={null}
					centered
					className={
						isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
					}
					closable={true}
					maskClosable={true}
				>
					<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
						{t('advancedFilter')}
					</div>

					<div className="flex flex-col sm:flex-row flex-wrap gap-x-4">
						<div className="w-full sm:w-[calc(50%-0.5rem)]">
							<div className="mb-[16px] flex flex-col gap-2">
								<Text className="text-xs text-gray-650">
									{t('filterByProposerName')}
								</Text>
								<Input
									className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
									value={proposerNameValue}
									allowClear
									onChange={onProposerNameChange}
								/>
							</div>
						</div>

						<div className="w-full sm:w-[calc(50%-0.5rem)]">
							<div className="mb-[16px] flex flex-col gap-2">
								<Text className="text-xs text-gray-650">
									{t('filterByProposerSRCMId')}
								</Text>
								<Input
									className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
									value={srcmIdProposer}
									allowClear
									onChange={onProposerSRCMIdChange}
								/>
								{proposerSrcmIdError && (
									<Text className="text-xs text-red-500">
										{t(proposerSrcmIdError)}
									</Text>
								)}
							</div>
						</div>
					</div>
					<div></div>
					<div className="flex justify-center">
						<Button
							className="block sm:hidden flex-1 h-9 text-primary-blue font-sans font-bold text-center"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}

								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					</div>
					<div
						className={`flex ${
							isStackVertical
								? 'flex-col items-center space-y-2'
								: 'justify-center gap-2'
						} mt-4 ant-modal-footer`}
					>
						<Button
							className="hidden sm:block flex-1 h-9 text-primary-blue font-sans font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
						<Button
							className={`flex-1 h-9 text-primary-blue font-sans font-bold ${
								isStackVertical ? 'p-2' : ''
							}`}
							onClick={onClose}
						>
							{t('closeFilter')}
						</Button>
						<Button
							type="primary"
							onClick={handleFilterButtonClick}
							className={`flex-1 h-9 font-sans font-bold ${
								isStackVertical ? 'p-2 mt-3' : ''
							}`}
							disabled={proposerSrcmIdError !== ''}
						>
							{t('applyFilter')}
						</Button>
					</div>
				</Modal>
			</div>
		</>
	);
};
